import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { logError } from "@entrilia/shared/logging";
import type { GridApiPremium } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useSelector } from "react-redux";
import type { ReportRow, TabularReport } from "../../api/types/widgetTypes";
import { impersonationSelector } from "../../store/state/user/selectors";
import NoDataResult from "../common/NoDataResult";
import TabularReportGrid from "../common/reports/TabularReportGrid";
import WidgetFootnote from "./WidgetFootnote";

interface Props {
  report: TabularReport | undefined;
  rows: ReportRow[];
  loading: boolean;
  noDataText?: string;
  footnote?: string;
  fetchError?: ApiError;
  gridApiRef: RefObject<GridApiPremium | null>;
}

const WidgetContent = ({ report, rows, loading, noDataText, footnote, fetchError, gridApiRef }: Props) => {
  const isImpersonation = useSelector(impersonationSelector);

  if (fetchError) {
    if (!isImpersonation) {
      logError(fetchError, "[WidgetContent] fetchError");
    }

    return <DataLoadingFailed title="Failed to load widget data" text={isImpersonation ? fetchError.message : ""} />;
  }

  if (noDataText) {
    return <NoDataResult text={noDataText} />;
  }

  if (report === undefined) {
    return <InlineLoader />;
  }

  return (
    <>
      <TabularReportGrid
        columns={report.columns}
        rows={rows}
        groupTotals={report.groupTotals}
        grandTotals={report.grandTotals}
        hasRowsWithDetails={report.hasRowsWithDetails}
        loading={loading}
        detailColumns={report.detailColumns}
        apiRef={gridApiRef}
      />

      {footnote && <WidgetFootnote text={footnote} />}
    </>
  );
};

export default WidgetContent;
